import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Folder, MagnifyingGlass } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";

export default function GlobalSearch({ isAdmin }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState("");
  const [projects, setProjects] = useState([]);
  const [invoices, setInvoices] = useState([]);

  const load = useCallback(async () => {
    if (loaded || loading) return;
    setLoading(true);
    setError("");
    try {
      const [projectResponse, invoiceResponse] = await Promise.all([
        api.get(isAdmin ? "/admin/projects" : "/projects"),
        api.get(isAdmin ? "/admin/payments" : "/invoices"),
      ]);
      setProjects(Array.isArray(projectResponse.data) ? projectResponse.data : []);
      setInvoices(Array.isArray(invoiceResponse.data) ? invoiceResponse.data : []);
      setLoaded(true);
    } catch {
      setError("Search is temporarily unavailable.");
    } finally {
      setLoading(false);
    }
  }, [isAdmin, loaded, loading]);

  const show = useCallback(() => {
    setOpen(true);
    load();
  }, [load]);

  useEffect(() => {
    const onKeyDown = (event) => {
      const target = event.target;
      const isTyping =
        target instanceof HTMLElement &&
        (target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName));
      const macCommandK = event.metaKey && event.key.toLowerCase() === "k";
      const slash = event.key === "/" && !isTyping && !event.ctrlKey && !event.metaKey && !event.altKey;
      if (macCommandK || slash) {
        event.preventDefault();
        show();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [show]);

  useEffect(() => {
    setLoaded(false);
    setProjects([]);
    setInvoices([]);
  }, [isAdmin]);

  const selectProject = (id) => {
    setOpen(false);
    navigate(`/dashboard/projects/${id}`);
  };

  const selectInvoice = () => {
    setOpen(false);
    navigate(isAdmin ? "/admin/payments" : "/dashboard/billing");
  };

  return (
    <>
      <button
        type="button"
        onClick={show}
        className="hidden md:flex items-center gap-2 flex-1 max-w-md h-9 px-3 rounded-md border border-black/10 bg-white/60 text-sm text-neutral-500 hover:bg-white focus:outline-none focus:ring-2 focus:ring-[#0B4DCC]/30"
        data-testid="global-search-trigger"
        aria-label="Search projects and invoices"
      >
        <MagnifyingGlass size={14} />
        <span>Search projects, invoices…</span>
        <span className="ml-auto flex items-center gap-1 fx-mono-tag" style={{ fontSize: "12px" }}>
          <span>/</span>
        </span>
      </button>

      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput
          placeholder="Search projects, invoices…"
          data-testid="global-search-input"
        />
        <CommandList data-testid="global-search-results">
          {loading && <div className="py-8 text-center text-sm text-neutral-500">Loading…</div>}
          {!loading && error && (
            <div className="py-8 text-center text-sm text-red-600">
              {error}
              <button
                type="button"
                className="block mx-auto mt-3 text-[#0B4DCC] underline"
                onClick={load}
              >
                Try again
              </button>
            </div>
          )}
          {!loading && !error && <CommandEmpty>No matching projects or invoices.</CommandEmpty>}
          {!loading && !error && projects.length > 0 && (
            <CommandGroup heading="Projects">
              {projects.map((project) => (
                <CommandItem
                  key={`project-${project.id}`}
                  value={`project ${project.title || ""} ${project.id || ""} ${project.user_name || ""} ${project.user_email || ""}`}
                  onSelect={() => selectProject(project.id)}
                  data-testid={`global-search-project-${project.id}`}
                >
                  <Folder size={16} />
                  <span className="truncate">{project.title || "Untitled project"}</span>
                  <span className="ml-auto text-xs text-neutral-500">{project.status}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          {!loading && !error && projects.length > 0 && invoices.length > 0 && <CommandSeparator />}
          {!loading && !error && invoices.length > 0 && (
            <CommandGroup heading="Invoices">
              {invoices.map((invoice) => (
                <CommandItem
                  key={`invoice-${invoice.id}`}
                  value={`invoice ${invoice.number || ""} ${invoice.project_title || ""} ${invoice.id || ""}`}
                  onSelect={selectInvoice}
                  data-testid={`global-search-invoice-${invoice.id}`}
                >
                  <FileText size={16} />
                  <span className="font-mono">{invoice.number || "Invoice"}</span>
                  <span className="truncate text-xs text-neutral-500">{invoice.project_title || ""}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
        </CommandList>
      </CommandDialog>
    </>
  );
}
